import {
  DatalistOption,
  MedicalCondition,
  MedicalConditionError
} from '../CommonTypes';
import { ButtonContinue } from '../Button/ButtonContinue';
import SearchTextInput from '../SerachTextInput/SearchTextInput';
import RadioSelection from '../Product/RadioSelection';
import { populateDatalist, renderRadioOptions } from '../HelperMethods';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useState } from 'react';

interface MedicalConditionsProps {
  medicalConditionTypes: DatalistOption[];
  medicalConditions: MedicalCondition[];
  errorMessages: MedicalConditionError[];
  onUpdateMedicalConditions: (medicalConditions: MedicalCondition[]) => void;
  brand: string;
}

const MedicalConditions = ({
  medicalConditionTypes,
  medicalConditions,
  errorMessages,
  onUpdateMedicalConditions,
  brand
}: MedicalConditionsProps) => {
  if (medicalConditions.length === 0)
    medicalConditions = [
      ...medicalConditions,
      {
        typeOfCondition: '',
        hasDVLANotified: '',
        canKeepLicence: ''
      } as MedicalCondition
    ];

  const radioOptions = ['Yes', 'No'];

  const [localMedicalConditionTypes, setLocalMedicalConditionTypes] = useState<
    DatalistOption[]
  >([]);
  const [showPopup, setShowPopup] = useState(false);
  const resetDatalist = () => {
    setLocalMedicalConditionTypes([]);
  };

  const onClickRemoveMedicalCondition = (index: number): void =>
    onUpdateMedicalConditions(medicalConditions.filter((c, i) => index !== i));

  const onClickAddAnotherMedicalCondition = (): void => {
    const newMedicalConditions = [
      ...medicalConditions,
      {
        typeOfCondition: '',
        hasDVLANotified: '',
        canKeepLicence: ''
      } as MedicalCondition
    ];
    onUpdateMedicalConditions(newMedicalConditions);
  };

  const handleSearchMedicalConditionTypes = (
    e: React.FormEvent<HTMLInputElement>,
    index: number
  ) => {
    const newTypeOfConditionKey = localMedicalConditionTypes.find(
      (opt) => opt.text === e.currentTarget.value
    )?.value;
    const updatedMedicalConditions = medicalConditions.map((c, i) =>
      i === index
        ? {
            ...c,
            typeOfCondition: e.currentTarget.value,
            typeOfConditionKey: newTypeOfConditionKey
          }
        : { ...c }
    );
    onUpdateMedicalConditions(updatedMedicalConditions);
    loadMedicalConditionTypesBySearch(e.currentTarget.value);
  };

  const loadMedicalConditionTypesBySearch = (value: string) => {
    if (value.length > 2)
      setLocalMedicalConditionTypes(
        medicalConditionTypes.filter((m) =>
          m.text.toLowerCase().includes(value.toLocaleLowerCase())
        )
      );
    else setLocalMedicalConditionTypes(populateDatalist([]));
  };

  const handleCanKeepLicenceChange = (value: string, index: number) => {
    const updatedMedicalConditions = medicalConditions.map((c, i) =>
      i === index ? { ...c, canKeepLicence: value } : { ...c }
    );
    if (value === 'No') {
      setShowPopup(true);
    }
    onUpdateMedicalConditions(updatedMedicalConditions);
  };

  const handleHasDVLANotifiedChange = (value: string, index: number) => {
    const updatedMedicalConditions = medicalConditions.map((c, i) =>
      i === index ? { ...c, hasDVLANotified: value } : { ...c }
    );
    onUpdateMedicalConditions(updatedMedicalConditions);
  };

  return (
    <>
      <div className='medical-conditions'>
        {medicalConditions.map((condition, index) => (
          <div key={`medical-condition-${index}`}>
            <div className='flex-row'>
              <div className='inline-control'>
                <div className='control-label'>Type of condition</div>
                <div className='control'>
                  <SearchTextInput
                    name='typeOfCondition'
                    className='input-text w250'
                    value={condition.typeOfCondition}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      handleSearchMedicalConditionTypes(e, index)
                    }
                    onLeave={resetDatalist}
                    readOnly={false}
                    listName={`type-of-condition-${index}`}
                    listData={localMedicalConditionTypes}
                    error={errorMessages?.at(index)?.typeOfCondition ?? ''}
                  />
                </div>
              </div>
              <div className='inline-control'>
                <RadioSelection
                  show={true}
                  selectionName={'Has DVLA been notified?'}
                  error={errorMessages?.at(index)?.hasDVLANotified ?? ''}
                  fontDisabledClass=''>
                  {renderRadioOptions(
                    radioOptions,
                    `hasDVLANotified-${index}`,
                    condition.hasDVLANotified,
                    (value) => {
                      handleHasDVLANotifiedChange(value, index);
                    }
                  )}
                </RadioSelection>
              </div>
            </div>
            <div className='flex-row'>
              <div className='inline-control'>
                <RadioSelection
                  show={true}
                  error={errorMessages?.at(index)?.canKeepLicence ?? ''}
                  selectionName={'Has the DVLA said you can keep the licence?'}
                  fontDisabledClass=''>
                  {renderRadioOptions(
                    radioOptions,
                    `canKeepLicence-${index}`,
                    condition.canKeepLicence,
                    (value) => {
                      handleCanKeepLicenceChange(value, index);
                    }
                  )}
                </RadioSelection>
              </div>
              {index > 0 && (
                <div
                  className='inline-control remove-button-wrapper'
                  style={{ alignContent: 'end' }}>
                  <ButtonContinue
                    name={`remove-medical-condition-${index}`}
                    text='Remove'
                    onClick={() => onClickRemoveMedicalCondition(index)}
                    additionalClassNames='btn-cancel'
                  />
                </div>
              )}
            </div>
            {medicalConditions.length - 1 === index &&
              medicalConditions.length < 3 && (
                <div className='add-another-button-wrapper'>
                  <ButtonContinue
                    name={`add-another-medical-condition-${index}`}
                    text={'Add another medical condition'}
                    onClick={onClickAddAnotherMedicalCondition}
                    additionalClassNames='btn-ok'
                  />
                </div>
              )}
          </div>
        ))}
      </div>
      <Modal
        role='modal-popup'
        isOpen={showPopup}
        className={`insurance-${brand}`}
        toggle={() => setShowPopup(false)}>
        <ModalHeader toggle={() => setShowPopup(false)}>
          We’re sorry
        </ModalHeader>
        <ModalBody>
          We will not be able to provide you with motor insurance cover if the
          DVLA has said you cannot keep your licence.
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text='Continue'
            onClick={() => setShowPopup(false)}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MedicalConditions;
